/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';

import { Button, Modal, ErrorMessage, notification } from '@atoms';
import { TextField, NumberField, CheckboxField } from '@molecules';
import { NO_MARGIN_BOTTOM } from '@constants';
import { useCreateCisUser } from '@api/hooks';

import Form from '../Form';

import { FORM_FIELDS } from './constants';

const AddCisUserModal = React.forwardRef(({ data = [], onRefetch }, ref) => {
  const [form] = Form.useForm(FORM_FIELDS);

  const initialValues = React.useMemo(() => form.deserialize(data), [form, data]);
  const [visible, setVisible] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const toggle = React.useCallback(
    (state) => setVisible((prevState) => (typeof state === 'boolean' ? state : !prevState)),
    [],
  );

  React.useImperativeHandle(ref, () => ({ toggle }));

  const [cisUserMutation] = useCreateCisUser({});

  const handleSubmit = React.useCallback(
    async (userInfo) => {
      setError(null);
      setLoading(true);
      try {
        await cisUserMutation({ values: userInfo });
        await onRefetch();
        notification.success({ message: 'Added successfully!' });
        toggle(false);
        form.resetFields();
      } catch (e) {
        notification.error({ message: 'An error occurred while adding' });
        setError(e);
      }
      setLoading(false);
    },
    [cisUserMutation, form, onRefetch, toggle],
  );

  React.useEffect(() => {
    if (visible) {
      setError(null);
    }
  }, [setError, visible]);

  return (
    <Modal
      visible={visible}
      title="Add user"
      initialValues={initialValues}
      footer={
        <>
          <Button key="submit" type="primary" onClick={form.submit} loading={loading}>
            ADD USER
          </Button>
          <Button key="back" onClick={toggle}>
            CANCEL
          </Button>
        </>
      }
    >
      <ErrorMessage error={error} variant="block" spaced />

      <Form form={form} onSerializedFinish={handleSubmit} layout="vertical">
        <Form.Item label="Name:" style={NO_MARGIN_BOTTOM}>
          <TextField
            {...FORM_FIELDS.FIRST_NAME}
            style={{
              display: 'inline-block',
              width: 'calc(50% - 10px)',
            }}
          />
          <TextField
            {...FORM_FIELDS.LAST_NAME}
            style={{
              display: 'inline-block',
              width: 'calc(50% - 10px)',
              marginLeft: '20px',
            }}
          />
        </Form.Item>
        <Form.Item style={NO_MARGIN_BOTTOM}>
          <TextField {...FORM_FIELDS.EMAIL} />
        </Form.Item>
        <Form.Item label="Company:" style={NO_MARGIN_BOTTOM}>
          <TextField {...FORM_FIELDS.COMPANY} />
        </Form.Item>
        <Form.Item label="Event ID:" style={NO_MARGIN_BOTTOM}>
          <NumberField {...FORM_FIELDS.EVENT_ID} />
        </Form.Item>
        <Form.Item
          label="Premium:"
          style={{ ...NO_MARGIN_BOTTOM, display: 'flex', flexDirection: 'row' }}
          labelAlign="right"
        >
          <CheckboxField {...FORM_FIELDS.PREMIUM} style={{ marginLeft: 10, marginTop: -5 }} />
        </Form.Item>
      </Form>
    </Modal>
  );
});

export default AddCisUserModal;
